import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import MovieGrid from "@/components/feature/MovieGrid";
import Pagination from "@/components/feature/Pagination";
import MovieFilterBar, { emptyFilters, type MovieFilters } from "@/components/feature/MovieFilterBar";
import SortTabs, { type SortKey } from "@/components/feature/SortTabs";
import { filterMovies, paginate, searchMovies, sortMovies } from "@/utils/movieQuery";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 12;

const HOT_KEYWORDS = ["暗夜之城", "悬疑", "陈屿舟", "中文字幕4K", "终局", "犯罪"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlQ = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(urlQ);
  const [filters, setFilters] = useState<MovieFilters>(emptyFilters);
  const [sort, setSort] = useState<SortKey>("latest");
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);

  useSeo({
    title: "搜索影片 - 饥渴的岳中文字幕",
    description: "在《饥渴的岳》中文字幕资源库中按关键词、导演、类型搜索影片，快速找到想看的中文字幕版本。",
    keywords: "饥渴的岳搜索,中文字幕搜索,影片检索,饥渴的岳资源查找",
  });

  useEffect(() => {
    setKeyword(urlQ);
  }, [urlQ]);

  useEffect(() => {
    setPage(1);
  }, [keyword, filters, sort]);

  const searched = useMemo(() => {
    const matched = searchMovies(movies, keyword);
    const filtered = filterMovies(matched, filters);
    return sortMovies(filtered, sort);
  }, [keyword, filters, sort]);

  const totalPages = Math.max(1, Math.ceil(searched.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = paginate(searched, currentPage, PAGE_SIZE);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    setSearchParams(q ? { q } : {});
  };

  const runKeyword = (word: string) => {
    setKeyword(word);
    setSearchParams({ q: word });
  };

  const resetFilters = () => setFilters(emptyFilters);
  const activeFilterCount = Object.values(filters).filter((v) => v !== "全部").length;

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-12">
      <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-400 font-label mb-6 flex-wrap">
        <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-200">搜索</span>
      </nav>

      <header className="mb-8 md:mb-10 max-w-3xl">
        <h1 className="font-heading text-foreground-50 text-2xl md:text-4xl font-bold mb-3">
          搜索中文字幕影片
        </h1>
        <p className="text-sm md:text-base text-foreground-400 font-label leading-relaxed">
          输入影片名称、导演、类型或字幕版本关键词，实时筛选《饥渴的岳》系列中文字幕资源。
        </p>
      </header>

      <form
        onSubmit={submit}
        className="flex items-center gap-2 bg-background-900 border border-background-700 rounded-lg md:rounded-full pl-4 pr-2 h-14 md:h-16 w-full max-w-3xl mb-6 focus-within:border-accent-600 transition-colors"
      >
        <i className="ri-search-line text-foreground-400 text-xl" />
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="输入关键词，例如：暗夜之城、陈屿舟、悬疑"
          className="flex-1 bg-transparent border-none outline-none text-sm md:text-base text-foreground-100 placeholder:text-foreground-500 font-label min-w-0"
        />
        {keyword && (
          <button
            type="button"
            onClick={() => {
              setKeyword("");
              setSearchParams({});
            }}
            aria-label="清空关键词"
            className="w-9 h-9 rounded-full text-foreground-400 hover:text-accent-400 flex items-center justify-center cursor-pointer shrink-0"
          >
            <i className="ri-close-circle-line text-lg" />
          </button>
        )}
        <button
          type="submit"
          className="h-10 md:h-12 px-4 md:px-6 rounded-md md:rounded-full bg-primary-600 hover:bg-primary-500 text-background-50 flex items-center gap-2 transition-colors cursor-pointer shrink-0 whitespace-nowrap"
        >
          <i className="ri-search-line" />
          <span className="hidden sm:inline text-sm font-label">搜索</span>
        </button>
      </form>

      <div className="flex flex-wrap items-center gap-2 mb-8">
        <span className="text-xs text-foreground-500 font-label">热门搜索：</span>
        {HOT_KEYWORDS.map((word) => (
          <button
            key={word}
            type="button"
            onClick={() => runKeyword(word)}
            className="px-3 py-1 rounded-full bg-background-900 border border-background-800 hover:border-accent-600 hover:text-accent-400 text-foreground-300 text-xs font-label transition-colors cursor-pointer whitespace-nowrap"
          >
            {word}
          </button>
        ))}
      </div>

      {urlQ ? (
        <p className="text-sm text-foreground-400 font-label mb-6">
          关键词「<span className="text-accent-500 font-semibold">{urlQ}</span>」共找到{" "}
          <span className="text-foreground-100 font-semibold">{searched.length}</span> 部影片
        </p>
      ) : (
        <p className="text-sm text-foreground-400 font-label mb-6">
          当前展示全部 <span className="text-foreground-100 font-semibold">{searched.length}</span> 部影片
        </p>
      )}

      <div className="lg:hidden mb-4">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="w-full flex items-center justify-center gap-2 h-11 rounded-md border border-background-700 text-foreground-100 font-label text-sm cursor-pointer whitespace-nowrap"
        >
          <i className="ri-filter-3-line text-accent-500" />
          {showFilters ? "收起筛选" : "展开筛选条件"}
          {activeFilterCount > 0 && (
            <span className="px-2 py-0.5 rounded-full bg-primary-600 text-background-50 text-xs">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>

      <div className={`${showFilters ? "block" : "hidden"} lg:block mb-6`}>
        <MovieFilterBar
          filters={filters}
          onChange={setFilters}
          onReset={resetFilters}
          resultCount={searched.length}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <p className="text-sm text-foreground-400 font-label">
          共 <span className="text-accent-500 font-semibold">{searched.length}</span> 条结果
        </p>
        <SortTabs value={sort} onChange={setSort} />
      </div>

      {pageItems.length > 0 ? (
        <>
          <MovieGrid movies={pageItems} />
          <Pagination current={currentPage} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <span className="w-16 h-16 rounded-full bg-background-900 text-foreground-500 flex items-center justify-center text-3xl mb-5">
            <i className="ri-search-eye-line" />
          </span>
          <h2 className="font-heading text-foreground-100 text-lg font-semibold mb-2">
            没有找到与「{urlQ || keyword}」相关的影片
          </h2>
          <p className="text-sm text-foreground-400 font-label mb-6 max-w-md">
            换个关键词试试，或看看下面这些热门推荐。
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2 mb-8 max-w-xl">
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => runKeyword(word)}
                className="px-3 py-1.5 rounded-full bg-background-900 border border-background-800 hover:border-accent-600 hover:text-accent-400 text-foreground-300 text-sm font-label transition-colors cursor-pointer whitespace-nowrap"
              >
                {word}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setFilters(emptyFilters);
                setSearchParams({});
              }}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line" />
              重置并浏览全部
            </button>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-700 hover:border-accent-600 hover:text-accent-400 text-foreground-100 font-label text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-4-line" />
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}